import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout } from "@/components/AuthLayout";
import { useAuth } from "@/context/AuthContext";
import { api, errorMessage } from "@/lib/api";
import { bankIdLoginUrl } from "@/lib/bankid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, Fingerprint, ShieldCheck, UserPlus, Link2, AlertTriangle, Users } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const ROLE_LABEL = { innsyn: "innsyn i", handtering: "håndtering av" };

/**
 * Landingsside etter BankID.
 *  - #token=…        innlogging fullført (kjent BankID-identitet) -> lagre token og gå videre
 *  - ?pending=…      første gang: velg «ny konto» eller «koble eksisterende konto» (passord bekreftes)
 *  - ?error=…        feil/avbrutt hos leverandøren
 */
export default function BankIdCompletePage() {
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const pendingId = params.get("pending") || "";
  const errorParam = params.get("error") || "";
  const next = params.get("next") || "";

  const [phase, setPhase] = useState("init"); // init | onboarding | error
  const [pending, setPending] = useState(null);
  const [mode, setMode] = useState("new");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(errorParam);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    const token = hash.get("token");
    if (token) {
      window.history.replaceState(null, "", window.location.pathname + window.location.search);
      const wanted = sessionStorage.getItem("rp_post_auth_redirect");
      sessionStorage.removeItem("rp_post_auth_redirect");
      sessionStorage.removeItem("rp_invite_token");
      loginWithToken(token)
        .then((me) => navigate(me?.role === "admin" ? "/admin" : (me?.pending_invitations > 0 ? "/familie" : wanted || next || "/"), { replace: true }))
        .catch(() => { setError("Innloggingen kunne ikke fullføres. Prøv igjen."); setPhase("error"); });
      return;
    }
    if (errorParam) { setPhase("error"); return; }
    if (!pendingId) { setError("Mangler informasjon fra BankID. Start innloggingen på nytt."); setPhase("error"); return; }
    api.get(`/auth/bankid/pending/${encodeURIComponent(pendingId)}`, { skipActAs: true })
      .then((r) => {
        setPending(r.data);
        const inviteEmail = r.data.invite?.email;
        setEmail(inviteEmail || r.data.email_hint || "");
        if (!r.data.registration_open) setMode("link");
        setPhase("onboarding");
      })
      .catch((err) => { setError(errorMessage(err, "Innloggingen er utløpt. Start på nytt.")); setPhase("error"); });
  }, [pendingId, errorParam, next, loginWithToken, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) return setError("Skriv inn e-postadressen din");
    if (mode === "link" && !password) return setError("Skriv inn passordet til den eksisterende kontoen");
    setBusy(true);
    try {
      const r = await api.post("/auth/bankid/complete", { pending_id: pendingId, mode, email: email.trim(), password: mode === "link" ? password : null }, { skipActAs: true });
      sessionStorage.removeItem("rp_invite_token");
      if (r.data.email_verification === "sendt") {
        toast.info(`Vi har sendt en bekreftelseslenke til ${email.trim()}`, { description: r.data.unlinked_invitations > 0 ? "Invitasjoner til fullmakt kobles til kontoen din når adressen er bekreftet." : "Klikk på lenken i e-posten for å bekrefte adressen.", duration: 9000 });
      }
      const me = await loginWithToken(r.data.access_token);
      navigate(r.data.pending_invitations > 0 || me?.pending_invitations > 0 ? "/familie" : "/", { replace: true });
    } catch (err) {
      setError(errorMessage(err, "Kunne ikke fullføre"));
    } finally {
      setBusy(false);
    }
  };

  if (phase === "init") {
    return (
      <AuthLayout title="Logger inn med BankID" subtitle="Et øyeblikk …">
        <div className="flex items-center justify-center py-10" data-testid="bankid-complete-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      </AuthLayout>
    );
  }

  if (phase === "error") {
    return (
      <AuthLayout title="BankID-innlogging" subtitle="Noe gikk galt." footer={<p><Link to="/logg-inn" className="text-primary font-medium hover:underline" data-testid="bankid-back-to-login">Tilbake til innlogging</Link></p>}>
        <Alert variant="destructive" data-testid="bankid-complete-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Innloggingen ble ikke fullført</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button className="w-full h-11 mt-5" onClick={() => window.location.assign(bankIdLoginUrl())} data-testid="bankid-retry-button"><Fingerprint className="h-4 w-4 mr-2" /> Prøv igjen med BankID</Button>
      </AuthLayout>
    );
  }

  const invite = pending?.invite;
  const firstName = (pending?.name || "").split(" ")[0];

  return (
    <AuthLayout
      title={firstName ? `Hei, ${firstName}` : "BankID bekreftet"}
      subtitle="Identiteten din er bekreftet med BankID. Er dette første gang du bruker Controlle, oppretter vi en konto til deg – eller du kan koble BankID til en konto du allerede har."
      footer={<p><Link to="/logg-inn" className="text-primary font-medium hover:underline">Avbryt</Link></p>}
    >
      <div className="mb-5 rounded-lg border bg-secondary/60 px-4 py-3 flex items-center gap-3" data-testid="bankid-identity-card">
        <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0"><ShieldCheck className="h-5 w-5" /></div>
        <div className="min-w-0">
          <p className="font-medium truncate" data-testid="bankid-identity-name">{pending?.name || "Bekreftet med BankID"}</p>
          <p className="text-xs text-muted-foreground">{pending?.birthdate ? `Født ${pending.birthdate} · ` : ""}Bekreftet av BankID</p>
        </div>
      </div>

      {invite ? (
        <Alert className="mb-5 border-primary/40 bg-accent/40" data-testid="bankid-invite-banner">
          <Users className="h-4 w-4" />
          <AlertTitle>{invite.owner_name} har invitert deg</AlertTitle>
          <AlertDescription>Bruk e-postadressen {invite.email} så kobles invitasjonen til {ROLE_LABEL[invite.role] || invite.role} regningene automatisk til kontoen din.</AlertDescription>
        </Alert>
      ) : null}

      <form onSubmit={submit} className="space-y-5" noValidate>
        <RadioGroup value={mode} onValueChange={setMode} className="grid gap-2" data-testid="bankid-mode-group">
          {pending?.registration_open ? (
            <label className={cn("flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-secondary/60", mode === "new" && "border-primary bg-accent/40")} data-testid="bankid-mode-new">
              <RadioGroupItem value="new" className="mt-0.5" />
              <div className="flex-1">
                <p className="font-medium flex items-center gap-2"><UserPlus className="h-4 w-4 text-primary" /> Opprett ny konto</p>
                <p className="text-xs text-muted-foreground">Navnet hentes fra BankID. Du trenger bare å oppgi e-postadressen din.</p>
              </div>
            </label>
          ) : null}
          <label className={cn("flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors hover:bg-secondary/60", mode === "link" && "border-primary bg-accent/40")} data-testid="bankid-mode-link">
            <RadioGroupItem value="link" className="mt-0.5" />
            <div className="flex-1">
              <p className="font-medium flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Koble til eksisterende konto</p>
              <p className="text-xs text-muted-foreground">Har du allerede en Controlle-konto med e-post og passord? Bekreft med passordet én gang, så bruker du BankID fremover.</p>
            </div>
          </label>
        </RadioGroup>

        <div className="space-y-2">
          <Label htmlFor="bankid-email">E-postadresse</Label>
          <Input id="bankid-email" type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="h-11" data-testid="bankid-email-input" />
        </div>
        {mode === "link" ? (
          <div className="space-y-2">
            <Label htmlFor="bankid-password">Passord til eksisterende konto</Label>
            <Input id="bankid-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11" data-testid="bankid-password-input" />
            <p className="text-xs text-muted-foreground">Passordet brukes kun til å bekrefte at kontoen er din.</p>
          </div>
        ) : null}
        {error ? <p className="text-sm text-destructive" role="alert" data-testid="bankid-complete-error">{error}</p> : null}
        <Button type="submit" className="w-full h-11" disabled={busy} data-testid="bankid-complete-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Fingerprint className="h-4 w-4 mr-2" />} {mode === "new" ? "Opprett konto" : "Koble konto og logg inn"}
        </Button>
      </form>
    </AuthLayout>
  );
}
